<!--
  A pull request's labels as chips (`LabelChip`), in GitHub's order. With
  `max`, the first `max` and then "+N" for the rest, which names them on
  hover: a row stays one line. Labels GitHub has beyond those read (the
  first LABELS_PER_PR) are counted in "+N" and said not to be loaded.
  Nothing at all for a pull request without labels.
-->
<script lang="ts">
  import type { Labels } from '../api/queries';
  import LabelChip from './LabelChip.svelte';

  interface Props {
    /** The labels read, and how many there are; absent when they were never read. */
    labels: Labels | undefined;
    /** How many chips to show before "+N"; all of them without it. */
    max?: number;
    /** Makes each chip a button that does this with its name. */
    onpick?: (name: string) => void;
    /** Whether the chips take the Tab key (buttons only). */
    tabbable?: boolean;
  }
  let { labels, max, onpick, tabbable = true }: Props = $props();

  const all = $derived(labels?.nodes ?? []);
  const shown = $derived(max === undefined ? all : all.slice(0, max));
  const rest = $derived(all.slice(shown.length));
  /** On GitHub, not read here. */
  const unread = $derived(Math.max(0, (labels?.totalCount ?? 0) - all.length));
  const more = $derived(rest.length + unread);
  const moreTitle = $derived(
    [rest.map((l) => l.name).join(', '), unread ? `${unread} more on GitHub, not loaded here` : ''].filter(Boolean).join('; and '),
  );
</script>

{#if all.length}
  <span class="label-list" role="list" aria-label="Labels"
    >{#each shown as label (label.name)}<span role="listitem"><LabelChip {label} {onpick} {tabbable} /></span>{/each}{#if more}<span class="label-more" role="listitem" title={moreTitle}
        >+{more}<span class="sr-only">{' '}more label{more === 1 ? '' : 's'}</span></span
      >{/if}</span
  >
{/if}

<style>
  .label-list {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 3px 4px;
    min-width: 0;
    vertical-align: middle;
  }
  .label-list > span {
    display: inline-flex;
    min-width: 0;
  }
  .label-more {
    padding: 0 2px;
    color: var(--muted);
    font-size: 11px;
    line-height: 18px;
    cursor: help;
    white-space: nowrap;
  }
</style>
